import {cancelSearchStep, getUnorderedSteps, scrollToLine, searchStep} from './steps'

import {debounce} from '@github/mini-throttle'
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github/selector-observer'
// eslint-disable-next-line no-restricted-imports
import {on} from 'delegated-events'

interface Match {
  stepIndex: number
  lineIndex: number
}

let matches: Match[] = []
let selectedMatchIndex = -1

function hasMatches() {
  return matches.length > 0
}

function totalMatches() {
  return matches.length
}

function goNext() {
  if (!hasMatches()) return
  goToMatch(selectedMatchIndex + 1 >= matches.length ? 0 : selectedMatchIndex + 1)
}

function goPrevious() {
  if (!hasMatches()) return
  goToMatch(selectedMatchIndex - 1 < 0 ? matches.length - 1 : selectedMatchIndex - 1)
}

function goToMatch(matchIndex: number) {
  const current = matches[selectedMatchIndex]!
  const match = matches[matchIndex]!
  const steps = getUnorderedSteps()
  const currentStep = steps[current.stepIndex]!
  const nextStep = steps[match.stepIndex]!

  currentStep.parser.setSelectedLine(-1)
  nextStep.parser.setSelectedLine(match.lineIndex)

  scrollToLine(nextStep, match.lineIndex, {select: true})

  selectedMatchIndex = matchIndex
}

function search(text: string) {
  let selectFirstLine = true
  matches = getUnorderedSteps().reduce((result: Match[], step, stepIndex) => {
    const lineIndexes = searchStep(step, text, selectFirstLine)
    if (lineIndexes.length > 0) selectFirstLine = false

    result.push(...lineIndexes.map(lineIndex => ({lineIndex, stepIndex})))
    return result
  }, [])
  selectedMatchIndex = 0
}

function cancelSearch() {
  matches = []
  selectedMatchIndex = -1
  for (const step of getUnorderedSteps()) {
    cancelSearchStep(step)
  }
}

observe('.js-check-steps-search', {
  constructor: HTMLElement,
  add(step) {
    configureCheckSteps(step)
  },
})

function configureCheckSteps(checkSteps: HTMLElement) {
  const toolbar = checkSteps.querySelector<HTMLElement>('.js-checks-log-toolbar')!
  const input = toolbar.querySelector<HTMLInputElement>('.js-checks-log-search-input')!

  input.addEventListener(
    'input',
    debounce(() => {
      if (!input.value) {
        cancelSearch()
      } else {
        search(input.value)
      }
      updateSearchUI(checkSteps, false)
    }, 150),
  )

  input.addEventListener('focus', () => updateSearchUI(checkSteps, false))
  input.addEventListener('blur', () => updateSearchUI(checkSteps, true))
}

function updateSearchUI(checkSteps: HTMLElement, hidden: boolean) {
  if (totalMatches() > 0) {
    showSearchUI(checkSteps)
  } else {
    disableSearchUI(checkSteps, hidden)
  }
}

function disableSearchUI(checkSteps: HTMLElement, hidden: boolean) {
  const toolbar = checkSteps.querySelector<HTMLElement>('.js-checks-log-toolbar')!
  const previous = toolbar.querySelector<HTMLButtonElement>('.js-checks-log-search-previous')!
  const next = toolbar.querySelector<HTMLButtonElement>('.js-checks-log-search-next')!
  const counter = toolbar.querySelector<HTMLSpanElement>('.js-checks-log-search-counter')!

  counter.textContent = '0/0'
  counter.hidden = hidden
  previous.disabled = true
  next.disabled = true
  previous.hidden = hidden
  next.hidden = hidden
}

function showSearchUI(checkSteps: HTMLElement) {
  const toolbar = checkSteps.querySelector<HTMLElement>('.js-checks-log-toolbar')!
  const previous = toolbar.querySelector<HTMLButtonElement>('.js-checks-log-search-previous')!
  const next = toolbar.querySelector<HTMLButtonElement>('.js-checks-log-search-next')!
  const counter = toolbar.querySelector<HTMLSpanElement>('.js-checks-log-search-counter')!
  const input = toolbar.querySelector<HTMLInputElement>('.js-checks-log-search-input')!

  counter.textContent = `${selectedMatchIndex + 1}/${totalMatches()}`
  counter.hidden = false
  previous.hidden = false
  next.hidden = false
  previous.disabled = !input.value
  next.disabled = !input.value
}

on('submit', '.js-check-steps-search form.js-checks-log-search', event => {
  event.preventDefault()
  goNext()
  updateSearchUI((event.target as Element).closest<HTMLElement>('.js-check-steps-search')!, false)
})

on('click', '.js-checks-log-search-previous', event => {
  goPrevious()
  showSearchUI((event.target as Element).closest<HTMLElement>('.js-check-steps-search')!)
})

on('click', '.js-checks-log-search-next', event => {
  goNext()
  showSearchUI((event.target as Element).closest<HTMLElement>('.js-check-steps-search')!)
})
